/**
 * Route Builder
 *
 * Converts an AppConfig into a React Router RouteObject[] tree.
 */

import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router-dom';
import { LoadingState } from '@org/design-system';
import type { AppConfig, EntityConfig } from '../types/app-config';
import { ShellLayout } from '../layout/ShellLayout';
import { ShadcnAppShell } from '../shadcn/ShadcnAppShell';
import { ComponentErrorBoundary } from './ComponentErrorBoundary';
import { AuthGuard } from '../auth/AuthGuard';
import { AuthProvider } from '../auth/auth-context';
import { RealtimeProvider } from '../realtime/realtime-context';
import { PluginRegistryProvider } from '../plugins/plugin-registry';
import { LocaleProvider, useTranslator } from '../i18n/locale-context';
import { BillingProvider } from '../billing/billing-context';
import {
  getLoginPath,
  getLogoutPath,
  isAuthEnabled,
  isExternalAuthUrl,
  toRoutePath,
} from '../auth/auth-paths';
import { LoginPage } from '../auth/LoginPage';
import { LogoutPage } from '../auth/LogoutPage';

const LazyGenericListPage = React.lazy(async () => ({
  default: (await import('../pages/GenericListPage')).GenericListPage,
}));

const LazyGenericDetailPage = React.lazy(async () => ({
  default: (await import('../pages/GenericDetailPage')).GenericDetailPage,
}));

const LazyGenericCreatePage = React.lazy(async () => ({
  default: (await import('../pages/GenericCreatePage')).GenericCreatePage,
}));

const LazyGenericEditPage = React.lazy(async () => ({
  default: (await import('../pages/GenericEditPage')).GenericEditPage,
}));

const LazyCustomPage = React.lazy(async () => ({
  default: (await import('../pages/CustomPage')).CustomPage,
}));

// =============================================================================
// Not Found Page
// =============================================================================

function NotFoundPage() {
  const t = useTranslator();
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
      <h1 className="text-4xl font-bold text-foreground">{t('route.notFoundCode')}</h1>
      <p className="text-muted-foreground">{t('route.notFound')}</p>
    </div>
  );
}

function withPageBoundary(element: React.ReactNode, errorTitle: string) {
  return (
    <ComponentErrorBoundary
      errorTitle={errorTitle}
      errorDescription="An unexpected error occurred while rendering this page."
    >
      {element}
    </ComponentErrorBoundary>
  );
}

function withLazyPageBoundary(
  element: React.ReactNode,
  loadingMessage: string,
  errorTitle: string,
) {
  return withPageBoundary(
    <React.Suspense fallback={<LoadingState message={loadingMessage} />}>
      {element}
    </React.Suspense>,
    errorTitle,
  );
}

// =============================================================================
// Route Builder
// =============================================================================

function buildEntityRoutes(entity: EntityConfig, apiBaseUrl: string): RouteObject[] {
  const childRoutes: RouteObject[] = [];

  if (entity.pages.includes('list')) {
    childRoutes.push({
      index: true,
      element: withLazyPageBoundary(
        <LazyGenericListPage entity={entity} apiBaseUrl={apiBaseUrl} />,
        `Loading ${entity.pluralName.toLowerCase()}...`,
        `Failed to render ${entity.pluralName}`,
      ),
    });
  }

  if (entity.pages.includes('create')) {
    childRoutes.push({
      path: 'new',
      element: withLazyPageBoundary(
        <LazyGenericCreatePage entity={entity} apiBaseUrl={apiBaseUrl} />,
        `Loading ${entity.name.toLowerCase()} form...`,
        `Failed to render ${entity.name} creation page`,
      ),
    });
  }

  if (entity.pages.includes('detail')) {
    childRoutes.push({
      path: ':id',
      element: withLazyPageBoundary(
        <LazyGenericDetailPage entity={entity} apiBaseUrl={apiBaseUrl} />,
        `Loading ${entity.name.toLowerCase()} details...`,
        `Failed to render ${entity.name} details page`,
      ),
    });
  }

  if (entity.pages.includes('edit')) {
    childRoutes.push({
      path: ':id/edit',
      element: withLazyPageBoundary(
        <LazyGenericEditPage entity={entity} apiBaseUrl={apiBaseUrl} />,
        `Loading ${entity.name.toLowerCase()} editor...`,
        `Failed to render ${entity.name} edit page`,
      ),
    });
  }

  if (!childRoutes.length) {
    return [];
  }

  return [
    {
      path: entity.slug,
      children: childRoutes,
    },
  ];
}

/**
 * Build a complete route tree from AppConfig.
 */
/**
 * Create a fully configured browser router from AppConfig.
 * This wraps createBrowserRouter so consuming apps don't need
 * to import react-router-dom directly.
 */
export function createAppRouter(config: AppConfig, options?: { useShadcnShell?: boolean }) {
  return createBrowserRouter(buildRoutes(config, options));
}

/**
 * Pre-built RuntimeProviders component.
 * Wraps children with all runtime context providers.
 */
export function RuntimeProviders({ config, children }: {
  config: AppConfig;
  children: React.ReactNode;
}) {
  return (
    <AuthProvider auth={config.auth}>
      <LocaleProvider config={config.i18n}>
        <BillingProvider config={config.billing}>
          <PluginRegistryProvider plugins={config.plugins}>
            <RealtimeProvider config={config.realtime}>
              {children}
            </RealtimeProvider>
          </PluginRegistryProvider>
        </BillingProvider>
      </LocaleProvider>
    </AuthProvider>
  );
}

/**
 * Pre-built RouterProvider component.
 * Consuming apps can render this directly without importing react-router-dom.
 */
export function AppRouterProvider({ config, useShadcnShell = false }: { config: AppConfig; useShadcnShell?: boolean }) {
  const router = React.useMemo(() => createAppRouter(config, { useShadcnShell }), [config, useShadcnShell]);
  return (
    <RuntimeProviders config={config}>
      <RouterProvider router={router} />
    </RuntimeProviders>
  );
}

export function buildRoutes(config: AppConfig, options?: { useShadcnShell?: boolean }): RouteObject[] {
  const authEnabled = isAuthEnabled(config.auth);
  const loginPath = getLoginPath(config.auth);
  const logoutPath = getLogoutPath(config.auth);

  // Entity CRUD routes (entities may be absent in ShadcnAppConfig)
  const entityRoutes = (config.entities ?? []).flatMap(entity =>
    buildEntityRoutes(entity, config.apiBaseUrl)
  );

  // Custom page routes
  const customPageRoutes: RouteObject[] = config.pages.map(page => ({
    path: page.path.replace(/^\//, ''), // Remove leading slash for relative paths
    element: withLazyPageBoundary(
      <LazyCustomPage pageConfig={page} apiBaseUrl={config.apiBaseUrl} />,
      `Loading ${page.title.toLowerCase()}...`,
      `Failed to render ${page.title}`,
    ),
  }));

  // Determine default redirect
  const defaultPath = config.sidebar[0]?.href?.replace(/^\//, '') || (config.entities?.[0]?.slug ?? '');
  const ShellComponent = options?.useShadcnShell ? ShadcnAppShell : ShellLayout;
  const shellElement = authEnabled
    ? (
      <AuthGuard auth={config.auth}>
        <ShellComponent config={config} />
      </AuthGuard>
    )
    : (
      <ShellComponent config={config} />
    );

  const authRoutes: RouteObject[] = [];
  if (authEnabled && !isExternalAuthUrl(loginPath)) {
    authRoutes.push({
      path: toRoutePath(loginPath),
      element: withPageBoundary(
        <LoginPage auth={config.auth} />,
        'Failed to render login page',
      ),
    });
  }

  if (authEnabled && !isExternalAuthUrl(logoutPath)) {
    authRoutes.push({
      path: toRoutePath(logoutPath),
      element: withPageBoundary(
        <LogoutPage auth={config.auth} />,
        'Failed to render logout page',
      ),
    });
  }

  return [
    ...authRoutes,
    {
      path: '/',
      element: withPageBoundary(shellElement, 'Failed to render application shell'),
      children: [
        // Default redirect
        {
          index: true,
          element: <Navigate to={`/${defaultPath}`} replace />,
        },
        // Entity routes
        ...entityRoutes,
        // Custom pages
        ...customPageRoutes,
        // 404
        {
          path: '*',
          element: withPageBoundary(<NotFoundPage />, 'Page error'),
        },
      ],
    },
  ];
}
